import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogContent, 
    DialogHeader, 
    DialogTitle,
    DialogDescription 
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Scale, ExternalLink, FileText, BookOpen, X } from 'lucide-react';
import api from '@/lib/api';

// Mevzuat referansını parse et
// Supports multiple formats:
// 1. Object: {law_id: "5549", articles: ["1", "2"], description: "..."}
// 2. Object: {law_id: "5549", article: "4", text: "..."}
// 3. String: "5549/4" or "6493 sayılı Kanun m.4" or "MASAK Genel Tebliği"
const parseRegulationRef = (ref) => {
    if (!ref) return null;
    
    // Object format - handle both 'articles' (array) and 'article' (single)
    if (typeof ref === 'object') {
        const lawId = ref.law_id || ref.lawId || null;
        
        // Handle articles array vs single article
        let articleNo = null;
        if (ref.articles && Array.isArray(ref.articles) && ref.articles.length > 0) {
            // Join articles array: ["1", "2", "3"] -> "1, 2, 3"
            articleNo = ref.articles.join(', ');
        } else if (ref.article) {
            articleNo = ref.article;
        } else if (ref.articleNo) {
            articleNo = ref.articleNo;
        }
        
        // Try to extract numeric law_id if format is like "6493-M18"
        let numericLawId = null;
        if (lawId) {
            const numMatch = lawId.match(/^(\d{4,5})/);
            if (numMatch) {
                numericLawId = numMatch[1];
            }
        }
        
        return { 
            lawId: numericLawId || lawId, 
            rawLawId: lawId,  // Keep original for display
            articleNo: articleNo,
            text: ref.text || ref.description || null
        };
    }
    
    // String formats
    // "5549/4" formatı
    const slashMatch = ref.match(/^(\d+)\/(\d+)$/);
    if (slashMatch) {
        return { lawId: slashMatch[1], articleNo: slashMatch[2] };
    }
    
    // "5549 sayılı Kanun m.4" formatı
    const kanunMatch = ref.match(/(\d+)\s*sayılı.*?(?:m\.|madde\s*)(\d+)/i);
    if (kanunMatch) {
        return { lawId: kanunMatch[1], articleNo: kanunMatch[2] };
    }
    
    // Sadece kanun numarası
    const numMatch = ref.match(/^(\d{4,5})$/);
    if (numMatch) {
        return { lawId: numMatch[1], articleNo: null };
    }
    
    return { lawId: null, articleNo: null, text: ref };
};

// Bilinen kanunlar ve yönetmelikler
const KNOWN_LAWS = {
    '5549': { name: 'Suç Gelirlerinin Aklanmasının Önlenmesi Hakkında Kanun', short: 'AML Kanunu' },
    '6493': { name: 'Ödeme ve Menkul Kıymet Mutabakat Sistemleri, Ödeme Hizmetleri ve Elektronik Para Kuruluşları Hakkında Kanun', short: '6493 sayılı Kanun' },
    '5411': { name: 'Bankacılık Kanunu', short: 'Bankacılık Kanunu' },
    '6698': { name: 'Kişisel Verilerin Korunması Kanunu', short: 'KVKK' },
    '5464': { name: 'Banka Kartları ve Kredi Kartları Kanunu', short: 'Kart Kanunu' },
    '7258': { name: 'Spor Müsabakalarında Bahis ve Şans Oyunları Düzenlenmesi Hakkında Kanun', short: '7258 sayılı Kanun' },
    'TY': { name: 'Suç Gelirlerinin Aklanmasının ve Terörün Finansmanının Önlenmesine Dair Tedbirler Hakkında Yönetmelik', short: 'Tedbirler Yönetmeliği' },
};

const RegulationModal = ({ reference, isOpen, onClose }) => {
    const [lawData, setLawData] = useState(null);
    const [articleData, setArticleData] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const parsed = parseRegulationRef(reference);
    const knownLaw = parsed?.lawId ? KNOWN_LAWS[parsed.lawId] : null;

    useEffect(() => {
        if (isOpen && parsed?.lawId) {
            loadLawData();
        }
        // loadLawData is defined in this component and only reads `parsed`;
        // re-running on `reference` change covers all observable inputs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, reference]);

    const loadLawData = async () => {
        if (!parsed?.lawId) return;
        
        setLoading(true);
        try {
            // Try to fetch from API
            const lawRes = await api.get(`/mevzuat/laws/${parsed.lawId}`).catch(() => null);
            if (lawRes?.data) {
                setLawData(lawRes.data);
            }
            
            if (parsed.articleNo) {
                // For articles array, take first one for API call
                const firstArticle = parsed.articleNo.split(',')[0]?.trim();
                if (firstArticle) {
                    const articleRes = await api.get(`/mevzuat/laws/${parsed.lawId}/articles/${firstArticle}`).catch(() => null);
                    if (articleRes?.data) {
                        setArticleData(articleRes.data);
                    }
                }
            }
        } catch (err) {
            console.error('Load law error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Check if lawId is numeric
    const isNumericLaw = parsed?.lawId && /^\d{4,5}$/.test(parsed.lawId);

    const getLawName = () => {
        if (lawData?.name) return lawData.name;
        if (knownLaw?.name) return knownLaw.name;
        // For non-numeric law_id (e.g., "TCMB Ödeme Hizmetleri"), use rawLawId directly
        if (!isNumericLaw && parsed?.rawLawId) return parsed.rawLawId;
        if (isNumericLaw && parsed?.lawId) return `${parsed.lawId} sayılı Kanun`;
        return parsed?.text || (typeof reference === 'string' ? reference : reference?.description || 'Mevzuat');
    };

    const getShortName = () => {
        if (lawData?.short_name) return lawData.short_name;
        if (knownLaw?.short) return knownLaw.short;
        return null;
    };

    // Get display label for law header
    const getLawLabel = () => {
        if (isNumericLaw) return `${parsed.lawId} sayılı Kanun`;
        if (parsed?.rawLawId) return parsed.rawLawId;
        return 'Düzenleme';
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Scale className="w-5 h-5 text-primary" />
                        Mevzuat Referansı
                    </DialogTitle>
                    <DialogDescription>
                        İlgili düzenleme detayları
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    {/* Law Info */}
                    <div className="p-4 rounded-lg bg-secondary/30 border border-border">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <BookOpen className="w-4 h-4 text-primary" />
                                    <span className="font-medium text-sm text-muted-foreground">
                                        {getLawLabel()}
                                    </span>
                                    {getShortName() && (
                                        <Badge variant="secondary" className="text-xs">
                                            {getShortName()}
                                        </Badge>
                                    )}
                                </div>
                                <h3 className="font-semibold text-lg">
                                    {getLawName()}
                                </h3>
                                {parsed?.articleNo && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Madde {parsed.articleNo}
                                    </p>
                                )}
                            </div>
                            {parsed?.lawId && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(`https://www.mevzuat.gov.tr/mevzuat?MevzuatNo=${parsed.lawId}`, '_blank')}
                                >
                                    <ExternalLink className="w-4 h-4 mr-1" />
                                    Mevzuat.gov.tr
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Article Content */}
                    {articleData?.full_text ? (
                        <div className="p-4 rounded-lg bg-background border border-border">
                            <h4 className="font-medium mb-2 flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                {articleData.title || 'Madde İçeriği'}
                            </h4>
                            <p className="text-sm whitespace-pre-line leading-relaxed">
                                {articleData.full_text}
                            </p>
                        </div>
                    ) : (
                        <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                            <p className="text-sm text-amber-600">
                                Madde içeriği henüz sisteme yüklenmemiş. 
                                Detaylı bilgi için mevzuat.gov.tr üzerinden inceleyebilirsiniz.
                            </p>
                        </div>
                    )}

                    {/* Quick Info */}
                    {knownLaw && (
                        <div className="text-xs text-muted-foreground p-3 bg-secondary/20 rounded-lg">
                            <strong>Not:</strong> {knownLaw.name}, finansal uyum açısından temel düzenlemelerden biridir.
                            Detaylı inceleme için BDDK, MASAK veya TCMB resmi kaynaklarını kullanabilirsiniz.
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Regulation reference badge component
export const RegulationBadge = ({ reference, onClick }) => {
    if (!reference) return null;
    
    const parsed = parseRegulationRef(reference);
    const knownLaw = parsed?.lawId ? KNOWN_LAWS[parsed.lawId] : null;
    
    // Check if lawId is numeric (for "X sayılı Kanun" format)
    const isNumericLaw = parsed?.lawId && /^\d{4,5}$/.test(parsed.lawId);
    
    // Display text priority:
    // 1. Known law short name (e.g., "AML Kanunu")
    // 2. For numeric law_id: "6493 sayılı Kanun"
    // 3. For text law_id: Use rawLawId or description directly (e.g., "TCMB Ödeme Hizmetleri")
    // 4. Fallback to original reference
    let displayText;
    if (knownLaw?.short) {
        displayText = knownLaw.short;
    } else if (isNumericLaw) {
        displayText = `${parsed.lawId} sayılı Kanun`;
    } else if (parsed?.rawLawId) {
        displayText = parsed.rawLawId;
    } else if (parsed?.text) {
        displayText = parsed.text;
    } else if (typeof reference === 'string') {
        displayText = reference;
    } else {
        displayText = reference?.description || reference?.law_id || 'Mevzuat';
    }
    
    return (
        <Badge 
            variant="outline" 
            className="cursor-pointer hover:bg-primary/10 transition-colors gap-1"
            onClick={onClick}
            data-testid="regulation-badge"
        >
            <Scale className="w-3 h-3" />
            {displayText}
            {parsed?.articleNo && ` m.${parsed.articleNo}`}
        </Badge>
    );
};

export default RegulationModal;
